import { Duration, Encounter, Extension } from "@medplum/fhirtypes";

import {
  EncounterTimeSpent,
  EncounterTimeSpentInput,
} from "@/encounters/types/encounter-time-spent";
import { Extensions } from "@/fhir/enums/extensions";
import { Systems } from "@/fhir/enums/systems";

const MINUTES_UNIT_CODE = "min";
const EMPTY_TIME_SPENT_DISPLAY = "--";
const TIME_SPENT_EXTENSION_URLS: string[] = [
  Extensions.EncounterFaceToFaceTime,
  Extensions.EncounterCareCoordinationTime,
];

/**
 * Reads the time spent recorded on an encounter.
 *
 * @param encounter Encounter that may carry the time-spent extensions and `length`.
 * @returns Face-to-face, care coordination and total minutes; each is undefined when not recorded.
 */
export function getEncounterTimeSpent(encounter: Encounter | undefined): EncounterTimeSpent {
  return {
    faceToFaceMinutes: getExtensionMinutes(encounter, Extensions.EncounterFaceToFaceTime),
    careCoordinationMinutes: getExtensionMinutes(
      encounter,
      Extensions.EncounterCareCoordinationTime,
    ),
    totalMinutes: encounter?.length?.value,
  };
}

/**
 * Adds the recorded minutes. A value that was not recorded counts as nothing, not as zero.
 *
 * @param values Face-to-face and care coordination minutes.
 * @returns Their sum, or undefined when neither value is recorded.
 */
export function sumEncounterTimeSpentMinutes(values: EncounterTimeSpentInput): number | undefined {
  const { faceToFaceMinutes, careCoordinationMinutes } = values;
  if (faceToFaceMinutes === undefined && careCoordinationMinutes === undefined) return undefined;

  return (faceToFaceMinutes ?? 0) + (careCoordinationMinutes ?? 0);
}

/**
 * Returns an encounter copy with the time-spent extensions and `length` replaced by `values`.
 * An unrecorded value drops its extension; `length` is dropped when neither value is recorded.
 *
 * @param encounter Encounter to copy.
 * @param values Face-to-face and care coordination minutes to persist.
 * @returns Encounter with the time spent applied.
 */
export function applyEncounterTimeSpent(
  encounter: Encounter,
  values: EncounterTimeSpentInput,
): Encounter {
  const otherExtensions = (encounter.extension ?? []).filter(
    (extension) => !TIME_SPENT_EXTENSION_URLS.includes(extension.url),
  );
  const timeSpentExtensions = [
    buildMinutesExtension(Extensions.EncounterFaceToFaceTime, values.faceToFaceMinutes),
    buildMinutesExtension(Extensions.EncounterCareCoordinationTime, values.careCoordinationMinutes),
  ].reduce<Extension[]>((extensions, extension) => {
    if (extension) extensions.push(extension);
    return extensions;
  }, []);
  const totalMinutes = sumEncounterTimeSpentMinutes(values);

  const updatedEncounter: Encounter = {
    ...encounter,
    extension: [...otherExtensions, ...timeSpentExtensions],
    length: totalMinutes === undefined ? undefined : toMinutesDuration(totalMinutes),
  };
  if (!updatedEncounter.extension?.length) delete updatedEncounter.extension;
  if (!updatedEncounter.length) delete updatedEncounter.length;

  return updatedEncounter;
}

/**
 * Formats a minute value for display.
 *
 * @param minutes Minutes, or undefined when not recorded.
 * @returns `"30min"`, or `"--"` when not recorded.
 */
export function formatTimeSpentMinutes(minutes: number | undefined): string {
  return minutes === undefined ? EMPTY_TIME_SPENT_DISPLAY : `${minutes}min`;
}

function getExtensionMinutes(encounter: Encounter | undefined, url: string): number | undefined {
  return encounter?.extension?.find((extension) => extension.url === url)?.valueDuration?.value;
}

function buildMinutesExtension(url: string, minutes: number | undefined): Extension | undefined {
  if (minutes === undefined) return undefined;

  return { url, valueDuration: toMinutesDuration(minutes) };
}

function toMinutesDuration(minutes: number): Duration {
  return {
    value: minutes,
    unit: MINUTES_UNIT_CODE,
    system: Systems.UOM,
    code: MINUTES_UNIT_CODE,
  };
}
